import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useModerationDetail } from '../hooks/useModerationDetail';
import { useModerationActions } from '../hooks/useModerationActions';
import { useModerationIntelligence } from '../hooks/useModerationIntelligence';
import { buildActionSpecs } from '../actions/specs';
import { VARIANT_REGISTRY } from '../variants/registry';
import { DetailHeader } from '../components/DetailHeader';
import { PersonCard } from '../components/PersonCard';
import { ModerationHistoryCard } from '../components/ModerationHistoryCard';
import { ModerationIntelligenceCard } from '../components/ModerationIntelligenceCard';
import { ModerationStateCard } from '../components/ModerationStateCard';
import { InternalNotesCard } from '../components/InternalNotesCard';
import { FeedbackAttachmentsCard } from '../components/FeedbackAttachmentsCard';
import { ActionPanel } from '../components/ActionPanel';
import { SendNotificationDialog } from '../components/SendNotificationDialog';

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  );
}

export function ModerationDetailPage() {
  // `type` defaults to "user" (CMP NavHost.kt:204); it is advisory — kind comes from the DTO (§4.4).
  const { moderationId = '' } = useParams();
  const navigate = useNavigate();

  const { report, isLoading, isError, refetch } = useModerationDetail(moderationId);
  const { intelligence, isLoading: intelligenceLoading } = useModerationIntelligence(moderationId);
  // Hook is called unconditionally; it tolerates a null report while loading.
  const actions = useModerationActions(report);

  if (isLoading) return <DetailSkeleton />;

  if (isError || !report) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-muted-foreground">Failed to load this report.</p>
        <Button variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const variant = VARIANT_REGISTRY[report.kind];
  const SummaryCard = variant.SummaryCard;
  const ContentCard = variant.ContentCard;
  const specs = buildActionSpecs(report, actions, intelligence);

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <DetailHeader report={report} onBack={() => void navigate(-1)} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* LEFT */}
        <div className="flex flex-col gap-4">
          <SummaryCard report={report} />
          <ModerationIntelligenceCard intelligence={intelligence} isLoading={intelligenceLoading} />
          {report.kind === 'FEEDBACK' && <FeedbackAttachmentsCard attachments={report.attachments} />}
          <ModerationHistoryCard history={report.history} />
          <InternalNotesCard actions={actions} />
        </div>

        {/* RIGHT */}
        <div className="flex flex-col gap-4">
          {variant.showReportedUserBesideReporter && report.reportedUser ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <PersonCard title="Reporter" person={report.reporter} />
              <PersonCard title="Reported User" person={report.reportedUser} />
            </div>
          ) : (
            <PersonCard title="Reporter" person={report.reporter} />
          )}

          {ContentCard && <ContentCard report={report} actions={actions} />}

          <ModerationStateCard
            caseId={report.id}
            postId={report.contentId ? Number(report.contentId) : undefined}
            postStatus={report.kind === 'CONTENT' ? report.content.status : undefined}
            isLoading={intelligenceLoading}
          />

          <ActionPanel specs={specs} disabled={actions.isPending} />
        </div>
      </div>

      <SendNotificationDialog
        open={actions.notifyDialogOpen}
        onOpenChange={(open) => {
          if (!open) actions.closeNotifyDialog();
        }}
        onSubmit={actions.submitNotification}
        isSubmitting={actions.isPending}
      />
    </div>
  );
}
